import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOOK_CHECKOUT_URL = "https://buy.stripe.com/4gM3cx6S24JHbunf3Za7C04";
const WORKBOOK_CHECKOUT_URL = "https://buy.stripe.com/9B6dRb0tE1xvdCvg83a7C02";

const BOOK_COVER_URL = "/book-cover-v2.webp";
const WORKBOOK_COVER_URL = "/workbook-cover-v2.webp";
const AUTHOR_NAME = "Gabriel Welch";

const pillars = [
  {
    title: "Rest & Reflect",
    text: "Slow down long enough to hear what your pain has been trying to say. In a world that pushes you to keep moving, this step calls you to pause, confront what has been buried, and begin healing with honesty.",
    icon: "R",
  },
  {
    title: "Intercede",
    text: "Invite God into the places you have been trying to fix on your own. Through prayer, surrender, and His presence, healing becomes more than self-help — it becomes restoration.",
    icon: "I",
  },
  {
    title: "Cry",
    text: "Release what you have been holding in. What stays buried often stays in control. This pillar creates space for grief, honesty, vulnerability, and the kind of release that makes room for peace.",
    icon: "C",
  },
  {
    title: "Exhort",
    text: "Rise in truth, strength, and boldness. After the breaking comes rebuilding. This pillar helps you stand up again with renewed faith, clearer identity, and courage for the road ahead.",
    icon: "E",
  },
];

const symptoms = [
  "Repeating the same relationship patterns",
  "Emotional shutdown or withdrawal",
  "Feeling numb, dry, or distant",
  "Walls that keep love out and keep pain in",
];

export default function RiceMethodLandingPage() {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      toast.error("Please enter your first name");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        first_name: form.first_name.trim(),
        email: form.email.trim(),
      });
      toast.success(data.message);
      setSubmittedMessage({ message: data.message });
      setForm({ first_name: "", email: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || "Please check your details and try again.");
      } else {
        toast.error(detail || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="landing-root">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(245,158,11,0.18),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_30%)]" />
        <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="fade-up">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-amber-300">
                The RICE Method · Healing Manual · by {AUTHOR_NAME}
              </p>
              <h1
                className="max-w-3xl text-4xl font-bold leading-[1.05] text-white md:text-6xl lg:text-7xl"
                data-testid="hero-headline"
              >
                You're Not Stuck…
                <br />
                <span className="italic text-amber-200">You're Unhealed.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-stone-300 md:text-xl">
                If you keep repeating the same emotional cycles, it's not random — it's a process no one ever showed you.
                Discover <span className="text-white font-medium">The RICE Method</span>: a step-by-step healing framework rooted in faith.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={BOOK_CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-amber-400 px-7 py-4 text-base font-semibold text-stone-950 shadow-lg shadow-amber-500/20 transition hover:scale-[1.02] hover:bg-amber-300"
                  data-testid="hero-cta-order"
                >
                  Order Signed Copy
                </a>
                <a
                  href="#order"
                  className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition hover:bg-white/10"
                  data-testid="hero-cta-explore"
                >
                  See what's inside
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-400">
                <span>· Faith-rooted healing</span>
                <span>· Emotional restoration</span>
                <span>· Actionable framework</span>
              </div>
            </div>

            <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="relative mx-auto max-w-md">
                <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-br from-amber-300/30 via-amber-500/10 to-transparent blur-2xl" />
                <div className="overflow-hidden rounded-[1.75rem] border border-amber-300/20 bg-stone-900 shadow-2xl shadow-black/60">
                  <img
                    src={BOOK_COVER_URL}
                    alt={`The RICE Method Healing Manual — book cover by ${AUTHOR_NAME}`}
                    className="block h-auto w-full"
                    data-testid="hero-book-cover"
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-4 rounded-2xl border border-white/10 bg-stone-900/90 px-4 py-3 text-sm text-stone-200 shadow-xl backdrop-blur">
                Now available · signed copies · book + workbook
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY THIS BOOK */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-10">
        <div className="grid gap-8 rounded-[2rem] border border-white/10 bg-white/[0.03] p-8 md:grid-cols-[0.95fr_1.05fr] md:p-12">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-amber-300">Why this book</p>
            <h2 className="mt-4 font-serif-display text-3xl font-bold text-white md:text-4xl">
              Unhealed pain doesn't disappear. It repeats.
            </h2>
            <p className="mt-5 text-base leading-8 text-stone-300">
              Many people live dormant, dry, and hardened — shaped by past wounds, disappointment, fear, and emotional survival patterns. The same walls built for protection often become the very prisons keeping healing out.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2" data-testid="symptoms-list">
            {symptoms.map((item, idx) => (
              <div
                key={item}
                className="rounded-2xl border border-white/10 bg-stone-900/60 p-5"
                data-testid={`symptom-${idx}`}
              >
                <p className="text-base font-medium leading-7 text-stone-100">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="mx-auto max-w-7xl px-6 py-8 md:px-10 md:py-12">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-amber-300">The RICE Pillars</p>
          <h2 className="mt-4 font-serif-display text-3xl font-bold text-white md:text-5xl">
            Healing isn't random. It's a process.
          </h2>
          <p className="mt-5 text-lg leading-8 text-stone-300">
            Just like rice must go through water, oil, and heat before it can fulfill its purpose — the life, strength, and healing inside of you must be activated through process.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4" data-testid="pillars-grid">
          {pillars.map((pillar, idx) => (
            <div
              key={pillar.title}
              className="group rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-7 transition hover:-translate-y-1 hover:border-amber-300/30"
              data-testid={`pillar-${idx}`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-400 font-serif-display text-xl font-bold text-stone-950">
                {pillar.icon}
              </div>
              <h3 className="mt-6 font-serif-display text-2xl font-semibold text-white">{pillar.title}</h3>
              <p className="mt-4 text-base leading-8 text-stone-300">{pillar.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ORDER — BOOK + WORKBOOK */}
      <section id="order" className="mx-auto max-w-7xl px-6 py-20 md:px-10">
        <div className="mb-10 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-amber-300">Order now</p>
          <h2 className="mt-4 font-serif-display text-3xl font-bold text-white md:text-5xl">
            Get your copy today.
          </h2>
          <p className="mt-5 text-lg leading-8 text-stone-300">
            The healing manual and its companion workbook are both available now as <span className="text-white font-medium">signed copies</span> — hand-signed by the author. Grab the book to begin the process, and the workbook to walk it out.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* MAIN BOOK */}
          <div
            className="flex flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.03] transition hover:border-amber-300/30"
            data-testid="product-book"
          >
            <div className="relative aspect-[2/3] w-full overflow-hidden bg-stone-900">
              <img
                src={BOOK_COVER_URL}
                alt="The RICE Method Healing Manual — main book cover"
                className="h-full w-full object-cover"
                data-testid="product-book-cover"
              />
              <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-stone-950/80 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-amber-200 backdrop-blur">
                <span aria-hidden="true">✍</span> Signed
              </span>
            </div>
            <div className="flex flex-1 flex-col p-7 md:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300">The main book · signed copy</p>
              <h3 className="mt-3 font-serif-display text-2xl font-bold text-white">
                The RICE Method: Healing Manual
              </h3>
              <p className="mt-3 text-base leading-7 text-stone-300">
                The foundational guide — identify hidden emotional walls, understand unresolved wounds, and begin a biblically grounded healing process built on the four RICE pillars. <span className="text-amber-200">Hand-signed by the author.</span>
              </p>
              <a
                href={BOOK_CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center rounded-full bg-amber-400 px-6 py-3.5 text-sm font-semibold text-stone-950 transition hover:bg-amber-300"
                data-testid="product-book-cta"
                style={{ marginTop: "1.5rem" }}
              >
                Order Signed Copy
              </a>
            </div>
          </div>

          {/* WORKBOOK */}
          <div
            className="flex flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.03] transition hover:border-amber-300/30"
            data-testid="product-workbook"
          >
            <div className="relative aspect-[2/3] w-full overflow-hidden bg-stone-900">
              <img
                src={WORKBOOK_COVER_URL}
                alt="The RICE Method Companion Workbook cover"
                className="h-full w-full object-cover"
                data-testid="product-workbook-cover"
              />
              <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-stone-950/80 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-amber-200 backdrop-blur">
                <span aria-hidden="true">✍</span> Signed
              </span>
            </div>
            <div className="flex flex-1 flex-col p-7 md:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300">The companion · signed copy</p>
              <h3 className="mt-3 font-serif-display text-2xl font-bold text-white">
                The RICE Method: Companion Workbook
              </h3>
              <p className="mt-3 text-base leading-7 text-stone-300">
                Self-evaluation, guided reflection, and a healing action plan designed to walk with you through every pillar of the RICE Method. Turn the reading into a real practice. <span className="text-amber-200">Hand-signed by the author.</span>
              </p>
              <a
                href={WORKBOOK_CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center rounded-full bg-amber-400 px-6 py-3.5 text-sm font-semibold text-stone-950 transition hover:bg-amber-300"
                data-testid="product-workbook-cta"
                style={{ marginTop: "1.5rem" }}
              >
                Order Signed Copy
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* STAY UPDATED */}
      <section id="waitlist" className="border-t border-white/10 bg-white/[0.02]">
        <div className="mx-auto max-w-3xl px-6 py-14 md:px-10">
          {submittedMessage ? (
            <div
              className="rounded-2xl border border-amber-300/30 bg-amber-400/10 p-6 text-left"
              data-testid="waitlist-success"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-200">
                You're on the list
              </p>
              <h3 className="mt-2 font-serif-display text-xl font-bold text-white">
                Thanks — we'll keep you posted.
              </h3>
              <p className="mt-2 text-stone-200">{submittedMessage.message}</p>
              <button
                type="button"
                onClick={() => setSubmittedMessage(null)}
                className="mt-4 inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                data-testid="waitlist-add-another"
              >
                Add another email
              </button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-[1fr_1.1fr] md:items-center">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300">Begin</p>
                <h3 className="mt-2 font-serif-display text-2xl font-bold text-white md:text-3xl">
                  This is your first step into transformation.
                </h3>
                <p className="mt-3 text-sm leading-7 text-stone-400">
                  Leave your email and we'll send you updates on new offerings, upcoming events, and any future deals we open up along the way.
                </p>
              </div>
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-3"
                data-testid="waitlist-form"
              >
                <input
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  placeholder="First name"
                  className="w-full rounded-full border border-white/10 bg-stone-900 px-5 py-3 text-white placeholder:text-stone-500 focus:border-amber-300 focus:outline-none"
                  data-testid="waitlist-input-first-name"
                  required
                />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Email address"
                  className="w-full rounded-full border border-white/10 bg-stone-900 px-5 py-3 text-white placeholder:text-stone-500 focus:border-amber-300 focus:outline-none"
                  data-testid="waitlist-input-email"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-stone-950 transition hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="waitlist-submit"
                >
                  {submitting ? "Joining..." : "Notify me"}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-white/10 px-6 py-8 text-center text-sm text-stone-500 md:px-10">
        © {new Date().getFullYear()} The RICE Method · {AUTHOR_NAME}. All rights reserved.
      </footer>
    </div>
  );
}
